import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Space, Modal, Alert, Typography, Spin, Empty } from 'antd';
import { QrcodeOutlined, SwapOutlined, CheckCircleOutlined } from '@ant-design/icons';
import QrScanner from '../qr/QrScanner';
import api from '../../services/api';
import { showErrorMessage } from '../../services/messages';

const { Text } = Typography;

const STORAGE_KEY = 'bluquimi_selected_machine';

/**
 * Mantido apenas para compatibilidade — a máquina NÃO é mais auto-hidratada do
 * dispositivo. A seleção persiste apenas dentro da cadeia "processar outro"
 * (via navigate state) e é reiniciada quando o operador volta à Home e inicia
 * o fluxo de novo.
 */
export const readStoredMachine = () => null;

/**
 * Seletor de máquina reutilizável (PWA do operador).
 *
 * A máquina selecionada NÃO é mais persistida no dispositivo. Ela permanece
 * selecionada apenas enquanto o operador permanece na mesma cadeia de fluxo
 * (ex.: "Processar outro tambor" repassa a máquina via navigate state). Ao
 * voltar para a Home e iniciar o fluxo de novo, o operador escolhe a máquina
 * novamente. O pai controla o valor via `value`/`onChange`.
 *
 * Props:
 *  - value: { id, code, name } controlado (opcional; se não informado, gerencia
 *           estado interno com o sticky do localStorage).
 *  - onChange(machine): chamado sempre que a máquina muda.
 *  - required: quando true e nenhuma máquina está selecionada, exibe aviso
 *              didático bloqueando o avanço do fluxo pai.
 *  - color: cor do fluxo (usada no destaque do card e botão primário).
 *  - compact: renderização mais compacta (padrão true).
 */
const MachineSelector = ({
  value,
  onChange,
  required = false,
  color = '#1b3663',
  compact = true,
}) => {
  const [internal, setInternal] = useState(() => value ?? null);
  const [qrVisible, setQrVisible] = useState(false);
  const [listVisible, setListVisible] = useState(false);
  const [machines, setMachines] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const selected = useMemo(() => value ?? internal, [value, internal]);

  // Sincroniza estado interno quando o pai passa `value`.
  useEffect(() => {
    if (value !== undefined) setInternal(value);
  }, [value]);

  const emitChange = (machine) => {
    setInternal(machine);
    if (onChange) onChange(machine);
  };

  const handleScanned = async (uid) => {
    try {
      const response = await api.machine.getByUid(uid);
      const m = response.data;
      emitChange({ id: m.id, code: m.code, name: m.name });
      setQrVisible(false);
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || 'Máquina não encontrada pelo QR.');
    }
  };

  const openList = async () => {
    setListVisible(true);
    setLoadingList(true);
    try {
      const response = await api.machine.list({ page: 1, pageSize: 100, isActive: 'true' });
      const rows = response.data?.rows || [];
      setMachines(rows);
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || 'Erro ao carregar máquinas.');
    } finally {
      setLoadingList(false);
    }
  };

  const handlePick = (machineId) => {
    const m = machines.find(x => x.id === machineId);
    if (m) {
      emitChange({ id: m.id, code: m.code, name: m.name });
      setListVisible(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {selected ? (
        <Card
          size="small"
          styles={{ body: { padding: compact ? 12 : 16 } }}
          style={{ borderColor: color, borderWidth: 1, background: '#fff' }}
          aria-label={`Máquina selecionada: ${selected.code} — ${selected.name}`}
        >
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space direction="vertical" size={0}>
              <Space>
                <CheckCircleOutlined style={{ color }} />
                <Text strong style={{ fontSize: compact ? 16 : 18 }}>{selected.code}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 13 }}>{selected.name}</Text>
            </Space>
            <Space>
              <Button
                icon={<QrcodeOutlined />}
                onClick={() => setQrVisible(true)}
                aria-label="Ler QR de outra máquina"
              >
                Ler QR
              </Button>
              <Button
                icon={<SwapOutlined />}
                onClick={openList}
                aria-label="Trocar de máquina pela lista"
              >
                Trocar
              </Button>
            </Space>
          </Space>
        </Card>
      ) : (
        <Card size="small" style={{ borderColor: color, borderWidth: 1 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text strong style={{ color }}>
              Selecione a máquina antes de continuar
            </Text>
            <Button
              type="primary"
              size="large"
              icon={<QrcodeOutlined />}
              onClick={() => setQrVisible(true)}
              aria-label="Ler QR da máquina"
              style={{ background: color, borderColor: color }}
              block
            >
              Ler QR da máquina
            </Button>
            <Button
              size="large"
              icon={<SwapOutlined />}
              onClick={openList}
              aria-label="Escolher máquina da lista"
              block
            >
              Escolher da lista
            </Button>
          </Space>
        </Card>
      )}

      {required && !selected && (
        <Alert
          type="warning"
          showIcon
          message="Uma máquina precisa ser selecionada para continuar."
        />
      )}

      {/* Modal de leitura de QR da máquina */}
      <Modal
        title="Ler QR da máquina"
        open={qrVisible}
        onCancel={() => setQrVisible(false)}
        footer={null}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Aponte a câmera para a etiqueta da máquina.
        </Text>
        <QrScanner onScan={handleScanned} />
      </Modal>

      {/* Modal de troca por lista — cards grandes touch-friendly (mesmo padrão
          visual da Home do PWA) em vez de dropdown: na fábrica, com luva/telas
          pequenas, o alvo de toque grande evita seleção errada. */}
      <Modal
        title="Escolher máquina"
        open={listVisible}
        onCancel={() => setListVisible(false)}
        footer={null}
        centered
      >
        {loadingList ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : machines.length === 0 ? (
          <Empty description="Nenhuma máquina ativa encontrada." />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: 12,
              maxHeight: '60vh',
              overflowY: 'auto',
              paddingRight: 4,
            }}
          >
            {machines.map((m) => {
              const isSelected = selected?.id === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handlePick(m.id)}
                  aria-label={`Selecionar máquina ${m.code} — ${m.name}`}
                  style={{
                    minHeight: 96,
                    border: isSelected ? '3px solid #fff' : 'none',
                    outline: isSelected ? `3px solid ${color}` : 'none',
                    borderRadius: 14,
                    background: color,
                    color: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '12px 10px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.16)',
                    transition: 'transform 0.08s ease',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                  onTouchStart={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
                  onTouchEnd={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
                  onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  <span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{m.code}</span>
                  <span style={{ fontSize: 13, opacity: 0.9, textAlign: 'center', lineHeight: 1.2 }}>
                    {m.name}
                  </span>
                  {isSelected && (
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      <CheckCircleOutlined /> Selecionada
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MachineSelector;
